import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Activity, LogOut, Menu, X, ArrowLeft, ArrowUpRight, PlayCircle } from "lucide-react";
import { useState } from "react";
import { dashboardPathFor } from "@/lib/uiConstants";
import SubscriptionBanner from "@/components/SubscriptionBanner";
import WelcomeOnboarding from "@/components/WelcomeOnboarding";

export const SONO_NAV = [
  { to: "/app/dashboard", label: "Dashboard" },
  { to: "/app/schedule", label: "Schedule" },
  { to: "/app/statistics", label: "Statistics" },
  { to: "/app/account", label: "Account" },
];

export const MANAGER_NAV = [
  { to: "/app/clinic", label: "Dashboard" },
  { to: "/app/clinic/statistics", label: "Statistics" },
  { to: "/app/clinic/stretching", label: "Therapy" },
  { to: "/app/clinic/reports", label: "Reports" },
  { to: "/app/clinic/sonographers", label: "Sonographer Management" },
];

export const ADMIN_NAV = [
  { to: "/admin", label: "Admin Console" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  const items = user?.role === "clinic_manager" ? MANAGER_NAV
    : user?.role === "admin" ? ADMIN_NAV
    : SONO_NAV;
  // Note: ternary kept for compact role → nav array mapping (no logic depth, easy to read)

  const goHome = () => nav("/");
  const showTour = () => window.dispatchEvent(new CustomEvent("sonote:show-tour"));
  const canSeeTour = user?.role === "sonographer" || user?.role === "clinic_manager";

  return (
    <div className="min-h-screen flex bg-zinc-50">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-zinc-200 bg-white">
        <button onClick={goHome} className="flex items-center gap-2 px-6 h-16 border-b border-zinc-200 text-left" data-testid="app-logo">
          <span className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "var(--ink)" }}>
            <Activity className="w-5 h-5" style={{ color: "var(--gold)" }} />
          </span>
          <span className="font-extrabold tracking-tight">Sonote<span style={{ color: "var(--gold)" }}>™</span></span>
        </button>
        <nav className="flex-1 p-4 space-y-1">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`appnav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `block px-4 py-3 rounded-lg text-sm font-semibold transition-colors ${
                  isActive ? "bg-zinc-900 text-white" : "text-zinc-700 hover:bg-zinc-100"
                }`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-zinc-200 space-y-2">
          <button onClick={goHome} className="w-full text-left text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1.5" data-testid="app-visit-public">
            <ArrowUpRight className="w-3.5 h-3.5" /> Visit public site
          </button>
          {canSeeTour && (
            <button onClick={showTour} className="w-full text-left text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1.5" data-testid="app-show-tour">
              <PlayCircle className="w-3.5 h-3.5" /> Show tour again
            </button>
          )}
          <div className="text-xs text-zinc-400">{user?.email}</div>
          <button onClick={async () => { await logout(); nav("/"); }} className="text-sm text-zinc-700 hover:text-zinc-900 flex items-center gap-1" data-testid="app-logout">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile topbar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-zinc-200">
        <div className="h-14 px-4 flex items-center justify-between">
          <button onClick={goHome} className="flex items-center gap-2" data-testid="app-logo-mobile">
            <span className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: "var(--ink)" }}>
              <Activity className="w-4 h-4" style={{ color: "var(--gold)" }} />
            </span>
            <span className="font-extrabold tracking-tight">Sonote™</span>
          </button>
          <button onClick={() => setOpen(!open)} data-testid="app-menu-toggle">{open ? <X /> : <Menu />}</button>
        </div>
        {open && (
          <div className="border-t border-zinc-200 p-4 flex flex-col gap-1 bg-white">
            {items.map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} className={({ isActive }) => `py-2.5 px-3 rounded-md text-sm font-semibold ${isActive ? "bg-zinc-900 text-white" : "text-zinc-700"}`}>{n.label}</NavLink>
            ))}
            {canSeeTour && (
              <button onClick={() => { setOpen(false); showTour(); }} className="mt-2 py-2.5 px-3 text-sm text-zinc-700 text-left flex items-center gap-2" data-testid="app-show-tour-mobile">
                <PlayCircle className="w-4 h-4" /> Show tour again
              </button>
            )}
            <button onClick={async () => { await logout(); nav("/"); }} className="mt-2 py-2.5 px-3 text-sm text-red-600 text-left">Sign out</button>
          </div>
        )}
      </div>

      <main className="flex-1 lg:pt-0 pt-14 min-w-0">
        <div className="p-4 sm:p-8 max-w-7xl mx-auto">
          <SubscriptionBanner />
          {children}
        </div>
      </main>
      <WelcomeOnboarding user={user} />
    </div>
  );
}

// Banner shown on public pages when user is authenticated
export function PublicAuthedBanner() {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  if (!user) return null;
  // Don't show on login/register or app routes
  if (loc.pathname.startsWith("/app") || loc.pathname.startsWith("/admin") ||
      loc.pathname.startsWith("/login") || loc.pathname.startsWith("/register")) return null;
  const dashPath = dashboardPathFor(user);
  return (
    <div className="bg-zinc-900 text-white">
      <div className="container-x py-2 flex items-center justify-between text-sm">
        <span className="text-zinc-300">Signed in as <span className="font-semibold text-white">{user.email}</span></span>
        <button onClick={() => nav(dashPath)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-semibold text-zinc-900" style={{ background: "var(--gold)" }} data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </button>
      </div>
    </div>
  );
}
